/*
 * The Command Center's state, shared with the classic panel.
 *
 * Three reads with three different speeds, so three hooks rather than one.
 *
 * `useCommandCenter` is the fast half — the awaiting queue and the four Operations lists — because
 * it answers "who is waiting right now" and must not sit behind anything slower.
 *
 * `useEventReadiness` is the twelve-read strip. It is deliberately separate, and so is what it
 * measures: readiness answers "was this configured", the action list answers "who is waiting". One
 * moves when a person fills in a form, the other when a buyer uploads a slip, and a single figure
 * carrying both would flicker with traffic. That split is `readiness.ts` rule 2 and it is why the
 * *figure* never mixes the two.
 *
 * TICK-148 — the *list* does. A new owner was told "ไม่มีอะไรค้างอยู่ในตอนนี้" one inch below a card
 * saying nothing could be sold, because the list only knew about queues. `setupActions` puts the
 * blocking checks that failed ahead of the queue items. It is still a list, not a score: it reads
 * `fail` only, so a check the token could not read never appears as a failure, and it is derived
 * from the same `EventReadiness` the strip draws so the two cannot disagree.
 *
 * `useRevenueTrend` is its own because the range is a control: changing it refetches one card, not
 * the page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi, ApiError, opsApi } from '../../../api';
import type {
  AdminOrderDto, AdminRevenueSeriesPointDto, AdminStatsDto, OpsCueDto, OpsDockSummaryDto,
  OpsRiskDto, OpsTaskDto, RevenueSeriesRange,
} from '../../../api';
import { actionableBlockers, computeReadiness, SELLING_PHASES } from '../../../readiness';
import type { EventReadiness, ReadinessInput, ReadinessTab } from '../../../readiness';
import { bandOf } from './useOpsRisks';
import { blockersOf } from './useOpsTasks';

const MINUTES = 60_000;
/** The one time constant on this screen: how long a handed-over slip may sit before it is late. */
export const SLIP_ATTENTION_MINUTES = 15;

/** The frame's CRITICAL / HIGH / MEDIUM / INFO. */
export type ActionSeverity = 'critical' | 'high' | 'medium' | 'info';

const SEVERITY_ORDER: Record<ActionSeverity, number> = { critical: 0, high: 1, medium: 2, info: 3 };

/**
 * A task's own priority, read as a severity.
 *
 * The organizer set these on the task; nothing here re-judges them. `urgent` is the only value that
 * reaches critical, which keeps the top band meaning "somebody said this was urgent" rather than
 * "this screen decided it was".
 */
export function severityOfPriority(priority: OpsTaskDto['priority']): ActionSeverity {
  return priority === 'urgent' ? 'critical'
    : priority === 'high' ? 'high'
      : priority === 'medium' ? 'medium' : 'info';
}

/** The worst priority in a set of tasks, as a severity. `info` for an empty set. */
function worstOf(tasks: readonly OpsTaskDto[]): ActionSeverity {
  return tasks.reduce<ActionSeverity>(
    (worst, t) => {
      const next = severityOfPriority(t.priority);
      return SEVERITY_ORDER[next] < SEVERITY_ORDER[worst] ? next : worst;
    },
    'info',
  );
}

export interface CommandAction {
  id: string;
  severity: ActionSeverity;
  label: string;
  /** Whose it is, where the record names one. Omitted rather than guessed. */
  owner: string | null;
  /** What is actually true, in the numbers that were counted. Never a deadline nobody stored. */
  consequence: string;
  tab: ReadinessTab | 'review';
  sub?: 'awaiting_verification' | 'problem';
}

export interface CommandInput {
  awaiting: readonly AdminOrderDto[];
  tasks: readonly OpsTaskDto[];
  risks: readonly OpsRiskDto[];
  stats: AdminStatsDto | null;
  /** Injected so the list is a function of its inputs and a test does not race the clock. */
  now?: number;
  today?: string;
}

/**
 * What needs a person, worst first.
 *
 * Every entry is a count this screen actually made. The frame writes its consequence lines as
 * deadlines — "ไม่ยืนยันภายใน 18:00 จะระงับการขาย" — and there is no such deadline in the system:
 * nothing suspends sales at a time, and inventing one would put a threat on an organizer's screen
 * that nothing enforces. What is said instead is what was counted, which is the part that is true.
 */
export function priorityActions(input: CommandInput): CommandAction[] {
  const now = input.now ?? Date.now();
  const today = input.today ?? new Date().toISOString().slice(0, 10);
  const out: CommandAction[] = [];

  /*
   * Age runs from `submittedAt` — when the buyer handed the slip over — not from when the order was
   * created. An order that sat unpaid for an hour and was paid a minute ago has not been waiting on
   * us for an hour.
   */
  const waited = input.awaiting
    .map((o) => (o.submittedAt ? now - new Date(o.submittedAt).getTime() : null))
    .filter((ms): ms is number => ms !== null && ms > SLIP_ATTENTION_MINUTES * MINUTES);

  if (waited.length > 0) {
    const longest = Math.round(Math.max(...waited) / MINUTES);
    out.push({
      id: 'slips_waiting',
      severity: 'critical',
      label: `ตรวจสลิปที่รอเกิน ${SLIP_ATTENTION_MINUTES} นาที`,
      owner: null,
      consequence: `ผู้ซื้อ ${waited.length} รายจ่ายเงินแล้วและยังรออยู่ · นานสุด ${longest} นาที`,
      tab: 'review',
      sub: 'awaiting_verification',
    });
  }

  if (input.stats && input.stats.problemCount > 0) {
    out.push({
      id: 'slips_problem',
      severity: 'critical',
      label: 'สลิปที่มีปัญหา ต้องตัดสินใจ',
      owner: null,
      consequence: `${input.stats.problemCount} รายการค้างอยู่ที่การตัดสินใจ ไม่ใช่ที่คิว`,
      tab: 'review',
      sub: 'problem',
    });
  }

  const unfinished = input.tasks.filter((t) => t.status !== 'done');
  const overdue = unfinished.filter((t) => t.slaDate < today);
  if (overdue.length > 0) {
    const oldest = overdue.map((t) => t.slaDate).sort()[0];
    out.push({
      id: 'tasks_overdue',
      severity: worstOf(overdue),
      label: 'งานที่เลยกำหนดส่งแล้ว',
      owner: ownerLabel(overdue),
      consequence: `${overdue.length} งาน · ค้างนานสุดตั้งแต่ ${oldest}`,
      tab: 'opsTasks',
    });
  }

  /*
   * Blocked is not late. A task waiting on another task is not the owner's fault and is not fixed
   * by chasing them — it is fixed by finishing the thing in front of it, which is why it is its own
   * line and counts only dependencies that are still unfinished.
   */
  const blockers = blockersOf(input.tasks);
  const blocked = unfinished.filter((t) => (blockers.get(t.id)?.length ?? 0) > 0);
  if (blocked.length > 0) {
    out.push({
      id: 'tasks_blocked',
      severity: worstOf(blocked),
      label: 'งานที่ติดอยู่เพราะงานอื่นยังไม่เสร็จ',
      owner: ownerLabel(blocked),
      consequence: `${blocked.length} งานรออยู่ · ปลดล็อกได้ด้วยการปิดงานที่ค้างอยู่ก่อนหน้า`,
      tab: 'opsTasks',
    });
  }

  const openRisks = input.risks.filter((r) => r.status === 'open' || r.status === 'mitigating');
  const topRisk = [...openRisks].sort((a, b) => b.score - a.score)[0];
  if (topRisk) {
    // The same band the register and the 5×5 matrix use, so no two screens disagree about a score.
    const band = bandOf(topRisk.score);
    out.push({
      id: 'risks_open',
      severity: band === 'low' ? 'info' : band,
      label: 'ความเสี่ยงที่ยังเปิดอยู่',
      owner: null,
      consequence: `${openRisks.length} เรื่อง · คะแนนสูงสุด ${topRisk.score} (${topRisk.title})`,
      tab: 'opsRisks',
    });
  }

  return out.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

/**
 * TICK-148 — blocking readiness checks that failed, as the first entries of the action list.
 *
 * Each one links to the screen `readiness.ts` already names for that check, so the list and the
 * strip beside it open the same place. Checks in the phases that decide whether anything can be
 * sold are `critical`; the preparation and show-day ones are `high`, because a missing run-sheet
 * does not stop a buyer paying.
 *
 * Not a failure, so not listed: a check that was `unreadable` (the token could not look) or not
 * applicable. A `sales` token gets nothing — it cannot open the screens these point at, and a link
 * that lands on a permission panel is worse than no link.
 */
export function setupActions(readiness: EventReadiness | null, role: string | null | undefined): CommandAction[] {
  if (role === 'sales' || !readiness) return [];
  return readiness.phases.flatMap((phase): CommandAction[] => {
    const severity: ActionSeverity = SELLING_PHASES.includes(phase.key) ? 'critical' : 'high';
    return actionableBlockers(readiness, [phase.key]).map((check) => ({
      id: `setup_${check.id}`,
      severity,
      label: check.label,
      owner: null,
      consequence: check.detail,
      tab: check.tab,
    }));
  });
}

/** The team a group of tasks belongs to, where they agree on one. Never invented. */
function ownerLabel(tasks: readonly OpsTaskDto[]): string | null {
  const names = new Set(
    tasks.map((t) => t.departmentName || t.ownerName || t.importedOwnerName).filter((n): n is string => !!n),
  );
  if (names.size === 1) return [...names][0];
  return names.size > 1 ? `${names.size} ฝ่าย` : null;
}

export interface CommandCenterState {
  awaiting: AdminOrderDto[];
  tasks: OpsTaskDto[];
  risks: OpsRiskDto[];
  cues: OpsCueDto[];
  docks: OpsDockSummaryDto[];
  /** False for a `sales` token: the four Operations reads are skipped, not made and swallowed. */
  withOps: boolean;
  loading: boolean;
  loadError: string;
  reload: () => Promise<void>;
}

const EMPTY_OPS = { tasks: [], risks: [], cues: [], docks: [] };

export function useCommandCenter(
  token: string,
  eventSlug: string | undefined,
  role: string | null,
): CommandCenterState {
  const [data, setData] = useState<Omit<CommandCenterState, 'withOps' | 'loading' | 'loadError' | 'reload'> | null>(null);
  const [loadError, setLoadError] = useState('');
  const withOps = role !== 'sales';

  const load = useCallback(async () => {
    try {
      const awaiting = await adminApi.listOrders(token, 'awaiting_verification', undefined, eventSlug);
      if (!withOps) {
        setData({ awaiting, ...EMPTY_OPS });
        setLoadError('');
        return;
      }
      const [tasks, risks, cues, docks] = await Promise.all([
        opsApi.listTasks(token, eventSlug),
        opsApi.listRisks(token, eventSlug),
        opsApi.listCues(token, eventSlug),
        opsApi.listDocks(token, eventSlug),
      ]);
      setData({ awaiting, tasks, risks, cues, docks });
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดข้อมูลของหน้านี้ไม่สำเร็จ');
    }
  }, [token, eventSlug, withOps]);

  useEffect(() => { void load(); }, [load]);

  return {
    awaiting: data?.awaiting ?? [],
    tasks: data?.tasks ?? [],
    risks: data?.risks ?? [],
    cues: data?.cues ?? [],
    docks: data?.docks ?? [],
    withOps,
    loading: !data && !loadError,
    loadError,
    reload: load,
  };
}

/**
 * The event's readiness, from up to twelve reads.
 *
 * A 403 is data here, not an error: four of these routes are owner-only and the whole `ops` family
 * answers a sales token 403, so a refusal becomes `null`, which `computeReadiness` reads as "this
 * person cannot see it" and reports as seen-in-part rather than as zero. Any other failure is still
 * an error — a screen that quietly downgraded a 500 to "you lack permission" would tell an owner
 * their event was unfinished when the truth is that a query broke.
 */
export interface EventReadinessState {
  readiness: EventReadiness | null;
  loading: boolean;
  error: string;
  /*
   * The event's end date and timezone, which ride on the ops settings record this hook already
   * reads as one of its twelve. Null for a token that cannot read ops, and for an event whose date
   * SuperAdmin has not set — the screens tell those two apart by `role`.
   */
  eventEndsAt: string | null;
  eventTimezone: string | null;
}

const permitted = async <T,>(p: Promise<T>): Promise<T | null> => {
  try {
    return await p;
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) return null;
    throw e;
  }
};

interface LoadedReadiness {
  readiness: EventReadiness;
  eventEndsAt: string | null;
  eventTimezone: string | null;
}

/** The twelve reads and what `computeReadiness` makes of them, shared by the strip and the landing check. */
async function loadEventReadiness(
  token: string,
  eventSlug: string | undefined,
  role: string | null,
): Promise<LoadedReadiness> {
  const owner = role === 'owner';
  const withOps = role !== 'sales';
  const [
    content, tickets, paymentAccounts, seatZones, ticketDelivery, salesChannels,
    opsSettings, staff, tasks, risks, cues, docks,
  ] = await Promise.all([
    permitted(adminApi.getContent(token, eventSlug)),
    permitted(adminApi.listTickets(token, eventSlug)),
    owner ? permitted(adminApi.listPaymentAccounts(token, eventSlug)) : null,
    owner ? permitted(adminApi.listSeatZones(token, eventSlug)) : null,
    owner ? permitted(adminApi.getTicketDelivery(token, eventSlug)) : null,
    owner ? permitted(adminApi.listSalesChannels(token, eventSlug)) : null,
    withOps ? permitted(opsApi.getEventSettings(token, eventSlug)) : null,
    withOps ? permitted(opsApi.listStaff(token, eventSlug)) : null,
    withOps ? permitted(opsApi.listTasks(token, eventSlug)) : null,
    withOps ? permitted(opsApi.listRisks(token, eventSlug)) : null,
    withOps ? permitted(opsApi.listCues(token, eventSlug)) : null,
    withOps ? permitted(opsApi.listDocks(token, eventSlug)) : null,
  ]);
  const input: ReadinessInput = {
    content, tickets, paymentAccounts, seatZones, ticketDelivery, salesChannels,
    opsSettings, staff, tasks, risks, cues, docks,
  };
  return {
    readiness: computeReadiness(input),
    eventEndsAt: opsSettings?.eventEndsAt ?? null,
    eventTimezone: opsSettings?.eventTimezone ?? null,
  };
}

/**
 * TICK-148 — where an owner lands on first login.
 *
 * The shell opens on the slip queue, which is right for an event that is already selling and empty
 * and useless for one that cannot sell yet. When the owner's event fails a blocking check in the
 * setup or sell phase, `onNotSellable` is called once so the shell can open the Command Center,
 * where the same checks are listed first.
 *
 * Deliberately narrow, so that nobody's usual landing changes without a reason:
 *  - owners only — other roles cannot fix what the checks name, and an unreadable check is never
 *    a reason (only `fail` counts, via `actionableBlockers`);
 *  - the selling phases only — a ready event with no run-sheet yet still opens on the queue;
 *  - best effort — if the reads fail, the default landing stands and the Command Center shows the
 *    error itself when the owner opens it.
 *
 * The caller decides whether the owner has already chosen a screen; this hook only reports.
 */
export function useReadinessLanding(
  token: string,
  eventSlug: string | undefined,
  role: string | null,
  onNotSellable: () => void,
): void {
  const latest = useRef(onNotSellable);
  useEffect(() => { latest.current = onNotSellable; });

  useEffect(() => {
    if (!token || role !== 'owner') return;
    let cancelled = false;
    loadEventReadiness(token, eventSlug, role)
      .then(({ readiness }) => {
        if (!cancelled && actionableBlockers(readiness, SELLING_PHASES).length > 0) latest.current();
      })
      .catch(() => { /* the default landing stands */ });
    return () => { cancelled = true; };
  }, [token, eventSlug, role]);
}

export function useEventReadiness(
  token: string,
  eventSlug: string | undefined,
  role: string | null,
): EventReadinessState {
  const [readiness, setReadiness] = useState<EventReadiness | null>(null);
  const [event, setEvent] = useState<{ endsAt: string | null; timezone: string | null }>(
    { endsAt: null, timezone: null },
  );
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const loaded = await loadEventReadiness(token, eventSlug, role);
      setReadiness(loaded.readiness);
      setEvent({ endsAt: loaded.eventEndsAt, timezone: loaded.eventTimezone });
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'อ่านความพร้อมของงานนี้ไม่สำเร็จ');
    }
  }, [token, eventSlug, role]);

  useEffect(() => { void load(); }, [load]);

  return {
    readiness,
    loading: !readiness && !error,
    error,
    eventEndsAt: event.endsAt,
    eventTimezone: event.timezone,
  };
}

export interface RevenueTrendState {
  series: AdminRevenueSeriesPointDto[] | null;
  range: RevenueSeriesRange;
  setRange: (next: RevenueSeriesRange) => void;
  error: string;
}

export function useRevenueTrend(token: string, eventSlug: string | undefined): RevenueTrendState {
  const [range, setRange] = useState<RevenueSeriesRange>('7d');
  const [series, setSeries] = useState<AdminRevenueSeriesPointDto[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSeries(null);
    adminApi.getRevenueSeries(token, range, eventSlug)
      .then((data) => { if (!cancelled) { setSeries(data); setError(''); } })
      .catch(() => { if (!cancelled) setError('โหลดแนวโน้มรายได้ไม่สำเร็จ'); });
    return () => { cancelled = true; };
  }, [token, range, eventSlug]);

  return { series, range, setRange, error };
}
